import { Link } from 'react-router-dom';
import type { Exam, Status } from '@/lib/types';
import { statusLabel } from '@/lib/ui';
import { ChevronRightIcon } from '@/components/ui/Icons';

interface ExamRowProps {
  exam: Exam;
  reportUrl?: string;
}

const scoreColor: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

const badgeColor: Record<Status, string> = {
  ok: 'bg-ok-tint text-ok-deep',
  warn: 'bg-warn-tint text-warn-deep',
  crit: 'bg-crit-tint text-crit-deep',
};

export function ExamRow({ exam, reportUrl }: ExamRowProps) {
  const panelLabel =
    exam.panelType === 'apex' ? 'Panel Optimal Apex' : 'Panel Optimal Core';

  const detailParts: string[] = [`${exam.biomarkerCount} biomarcadores`];
  if (exam.biomarkerCountTotal) {
    detailParts.push(
      `${exam.biomarkerCountTotal} totales${exam.biomarkerCountTotal !== exam.biomarkerCount ? ' con derivados' : ''}`
    );
  }
  if (exam.notes) detailParts.push(exam.notes);
  detailParts.push(exam.id);

  return (
    <Link
      to={reportUrl ?? '#'}
      className="grid grid-cols-[60px_1fr_auto_auto_12px] gap-3.5 items-center px-5 py-3.5 border-b border-line last:border-b-0 transition-colors hover:bg-hover max-[760px]:grid-cols-[50px_1fr_auto_12px] max-[760px]:grid-rows-[auto_auto] max-[760px]:gap-x-3 max-[760px]:gap-y-1 max-[760px]:px-4 max-[760px]:py-3"
    >
      <div className="text-left tnum max-[760px]:row-span-2 max-[760px]:self-center">
        <div className="text-[17px] font-bold tracking-[-0.02em] leading-none">
          {exam.collectedDay}
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mt-0.5">
          {exam.collectedMonth}
        </div>
      </div>
      <div className="max-[760px]:col-start-2 max-[760px]:row-span-2 max-[760px]:self-center">
        <div className="text-sm font-semibold">{panelLabel}</div>
        <div className="text-xs text-tertiary mt-px">{detailParts.join(' · ')}</div>
      </div>
      <div className="text-right tnum shrink-0 min-w-9 max-[760px]:col-start-3 max-[760px]:row-start-1">
        <span className={`text-base font-bold tracking-[-0.02em] leading-none ${scoreColor[exam.status]}`}>
          {exam.optimalScore}
        </span>
      </div>
      <span
        className={`inline-flex text-[10px] font-semibold tracking-[0.02em] px-1.5 py-0.5 rounded-sm whitespace-nowrap shrink-0 ${badgeColor[exam.status]} max-[760px]:col-start-3 max-[760px]:row-start-2 max-[760px]:justify-self-end`}
      >
        {statusLabel[exam.status]}
      </span>
      <ChevronRightIcon className="w-3 text-faint shrink-0 max-[760px]:row-span-2 max-[760px]:self-center" />
    </Link>
  );
}
